import {
  billOfQuantitiesApi,
  BillOfQuantitiesRequest,
} from "../api/billOfQuantities";
import { ItemLibraryResponse } from "../api/itemLibrary";
import { retainingWallsBuildUpApi, RetainingWallsBuildUpSpecification, RetainingWallsBuildUpGetResponse } from "../api/retainingWallsBuildUp";
import { geoToberWallBuildUpApi, GeoToberWallBuildUpSpecification, GeoToberWallBuildUpGetResponse } from "../api/wallSectionBuildUp";
import { buildRetainingWallSpecsFromState, buildGeoToberWallSpecsFromState } from "./wallsBuildUpSave";
import { TenderState } from "../types/tender.types";
import { transformToPayload } from "./storage";
import { queryClient } from "../App";
import { BOQ_QUERY_KEYS } from "../hooks/useBillOfQuantities";
import { buildGroundworksItemLibrary } from "./groundworksItemLibraryBuilder";
import { computeGroundworksBoqSummary } from "./groundworksBoqSummaryComputer";
import { computeRoadsAndSewersBoqSummary } from "./roadsAndSewersBoqSummaryComputer";
import { calculateSumAnalysisGrandTotal } from "./sumAnalysisGrandTotalCalculator";
import { calculateBoqTenderTotal } from "./boqTotals";
import { getScopedCurrentVersionNumber, getBoqVersionRoutePrefix, getNumericBoqVersion } from "./boqVersionPrefix";
import {
  serializeDynamicBillSummaryTabs,
  flattenCutFillsExtraItemsToBoqLines,
  flattenDrainageExtraItemsToBoqLines,
  deserializeDynamicBillSummaryTabs,
  deserializeCutFillsExtraItems,
  deserializeDrainageExtraItems,
} from "./dynamicBillSummarySerialization";
// Roads & Sewers' own fixed-summary-key filtering (kept separate from the
// import above so touching Roads & Sewers' Dynamic Bill Summary support can
// never change groundworks' own dynamicBillSummarySerialization.ts behaviour —
// see that file's own doc comment).
import { deserializeRoadsAndSewersDynamicBillSummaryTabs } from "./roadsAndSewersDynamicBillSummarySerialization";

/**
 * Interface for BOQ version info stored in localStorage
 */
export interface BoqVersionInfo {
  projectName: string;
  version: number;
}

/**
 * Forcefully update both BOQ and BOQ-versions endpoints
 * This is a simplified version that always calls both APIs
 * @param tenderState - Current tender state
 * @param changeLog - Change log message
 * @param groundworksTotals - Pre-calculated totals object from useGroundworksTotalCalculations hook
 * @param userName - Name of the logged-in user who is making the change
 */
export const forceSaveBothBoqEndpoints = async (
  tenderState: TenderState,
  changeLog: string = "Auto-saved changes",
  groundworksTotals?: {
    grandTotal: number; // BASE total before adjustments
    grandTotalWithAdjustments: number; // Total with prelims and OHP
    preliminariesAmount: number; // Calculated preliminaries amount
    ohpAmount: number; // Calculated OH&P amount
    discountAmount: number; // Calculated discount amount
    tenderTotal: number; // FINAL total after discount
    preliminariesPercent: number;
    ohpPercent: number;
    discountPercent: number;
  },
  userName?: string,
  itemLibraryData?: ItemLibraryResponse,
  opportunityNo?: string,
  // Pages nested under /roads-sewers or /groundworks leave this undefined and
  // get route-based prefix detection (both here and in the getCurrentBoqVersion/
  // updateVersion calls below). Callers that aren't route-scoped — e.g.
  // /tender/import-takeoff's per-job-type import handlers, which know exactly
  // which job type's version they're writing but aren't nested under either
  // route — must pass this explicitly, or the resolved/PUT version silently
  // comes back as a bare number instead of GWKS-N/RS-N.
  versionPrefixOverride?: "RS" | "GWKS" | null
): Promise<{ version: number }> => {
  console.log("🚀🚀🚀 FORCE SAVE FUNCTION CALLED 🚀🚀🚀");
  console.log(
    "🚀 FORCE SAVE: Starting mandatory BOQ and BOQ-versions update..."
  );
  console.log("👤 Last modified by:", userName || "Unknown");

  // Build state from groundworksTotals if provided
  let stateToSave = tenderState;

  if (groundworksTotals) {
    console.log("✅ Building state from hook-calculated totals:", {
      grandTotal: groundworksTotals.grandTotal,
      preliminariesAmount: groundworksTotals.preliminariesAmount,
      ohpAmount: groundworksTotals.ohpAmount,
      discountAmount: groundworksTotals.discountAmount,
      grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
      tenderTotal: groundworksTotals.tenderTotal,
      preliminariesPercent: groundworksTotals.preliminariesPercent,
      ohpPercent: groundworksTotals.ohpPercent,
      discountPercent: groundworksTotals.discountPercent,
    });

    // Build state using ALL values from groundworksTotals
    stateToSave = {
      ...tenderState,
      groundworks: {
        ...tenderState.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tenderState.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal, // FINAL total after all adjustments
        },
      },
      globalSettings: {
        ...tenderState.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      },
    };

    console.log("📝 State built from groundworksTotals:", {
      adjustments: stateToSave.groundworks.adjustments,
      totals: stateToSave.groundworks.totals,
      globalSettings: stateToSave.globalSettings.groundworks,
    });
  } else {
    console.log("⚠️ No groundworksTotals provided - using tenderState as-is");
  }

  // First, get base payload from transformToPayload
  const basePayload = transformToPayload(stateToSave);
  const projectName = stateToSave.projectName || stateToSave.siteAddress;

  console.log("📋 Project name from state:", projectName);

  // Fetch the currently-persisted BOQ document once, up front — used below
  // both as the dynamicBillSummaryTabs/cutFillsExtraItems fallback (a caller
  // whose state.groundworks was never hydrated with those fields, e.g.
  // StepperNavigation's "Calculate & Review" fired from a workflow step page
  // other than /groundworks/review, would otherwise send a `summary` object
  // missing those keys entirely, and this PUT/POST is a full-document
  // overwrite — silently deleting previously-saved dynamic tabs / Cut & Fill
  // extra items) and later for the boqExists check, so we only hit the API
  // once instead of twice.
  let _existingBoqResult: Awaited<ReturnType<typeof billOfQuantitiesApi.getByProject>> | null = null;
  if (projectName) {
    try {
      _existingBoqResult = await billOfQuantitiesApi.getByProject(projectName, opportunityNo);
    } catch {
      // 404 or network error — first-ever save, nothing to fall back to
    }
  }
  const _existingGroundworksSummary = (_existingBoqResult?.success && _existingBoqResult?.data)
    ? ((_existingBoqResult.data as any)?.billOfQuantities?.[0]?.groundworks?.summary as Record<string, unknown> | undefined)
    : undefined;
  // Roads & Sewers counterpart of _existingGroundworksSummary above — same
  // "state may never have been hydrated with this field" fallback for
  // roadsAndSewersDynamicBillSummaryTabs (a save fired from a workflow step
  // page other than /roads-sewers/review would otherwise send a `summary`
  // object with no dynamic tab keys at all, silently deleting them, since
  // this PUT/POST is a full-document overwrite).
  const _existingRoadsAndSewersSummary = (_existingBoqResult?.success && _existingBoqResult?.data)
    ? ((_existingBoqResult.data as any)?.billOfQuantities?.[0]?.roadAndSewers?.summary as Record<string, unknown> | undefined)
    : undefined;
  // Only fall back when the caller's state never carried the field at all
  // (undefined) — an explicit `[]`/`{}` means the user genuinely cleared it
  // and that intent must be respected, not overwritten from the API.
  const _resolvedDynamicTabs = stateToSave.groundworks?.dynamicBillSummaryTabs
    ?? deserializeDynamicBillSummaryTabs(_existingGroundworksSummary);
  const _resolvedRoadsAndSewersDynamicTabs = stateToSave.roadsAndSewersDynamicBillSummaryTabs
    ?? deserializeRoadsAndSewersDynamicBillSummaryTabs(_existingRoadsAndSewersSummary);
  // Roads & Sewers' own ad-hoc extra items (useBillSummaryExtraItems.tsx, reused
  // as-is) — same existing-BOQ fallback reasoning as _resolvedCutFillsExtraItems
  // etc below, one per fixed R&S Bill Summary domain.
  const _resolvedPrelimsExtraItems = stateToSave.prelimsExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.prelims);
  const _resolvedFoulSewersExtraItems = stateToSave.foulSewersExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.foulSewers);
  const _resolvedPumpStationExtraItems = stateToSave.pumpStationExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.pumpStation);
  const _resolvedRisingMainExtraItems = stateToSave.risingMainExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.risingMain);
  const _resolvedStormSewersExtraItems = stateToSave.stormSewersExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.stormSewers);
  const _resolvedPondsSwalesExtraItems = stateToSave.pondsSwalesExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.pondsSwales);
  const _resolvedCellularAttenuationExtraItems = stateToSave.cellularAttenuationExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.cellularAttenuation);
  const _resolvedWeholiteAttenuationExtraItems = stateToSave.weholiteAttenuationExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.weholiteAttenuation);
  const _resolvedConcAttenuationExtraItems = stateToSave.concAttenuationExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.concAttenuation);
  const _resolvedBoxCulvertsExtraItems = stateToSave.boxCulvertsExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.boxCulverts);
  const _resolvedRoadsExtraItems = stateToSave.roadsExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.roads);
  const _resolvedMainServicesExtraItems = stateToSave.mainServicesExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.mainServices);
  const _resolvedSubstationExtraItems = stateToSave.substationExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.substation);
  const _resolvedPosExtraItems = stateToSave.posExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.pos);
  const _resolvedCompoundExtraItems = stateToSave.compoundExtraItems
    ?? deserializeCutFillsExtraItems(_existingRoadsAndSewersSummary?.compound);
  const _resolvedCutFillsExtraItems = stateToSave.groundworks?.cutFillsExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.cutFills);
  const _resolvedFoundationsExtraItems = stateToSave.groundworks?.foundationsExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.foundations);
  const _resolvedDrainageExtraItems = stateToSave.groundworks?.drainageExtraItems
    ?? deserializeDrainageExtraItems(_existingGroundworksSummary?.drainage);
  const _resolvedPlotDrivesExtraItems = stateToSave.groundworks?.plotDrivesExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.plotDrives);
  const _resolvedPlotServiceExtraItems = stateToSave.groundworks?.plotServiceExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.plotService);
  const _resolvedScaffoldExtraItems = stateToSave.groundworks?.scaffoldExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.scaffold);
  const _resolvedPlotPavingsExtraItems = stateToSave.groundworks?.plotPavingsExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.plotPavings);
  const _resolvedGardenExtraItems = stateToSave.groundworks?.gardenExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.garden);
  const _resolvedRetainingWallsExtraItems = stateToSave.groundworks?.retainingWallsExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.retainingWalls);
  const _resolvedGeoWallsExtraItems = stateToSave.groundworks?.geoWallsExtraItems
    ?? deserializeCutFillsExtraItems(_existingGroundworksSummary?.geoWalls);

  // Resolve wall build-up specs for this save. On the first save, wall specs may not
  // yet be in the API (saveBothWallsBuildUp runs after this function), so check the
  // React Query cache first — it is populated when the user visits the wall pages.
  // Only fall back to an API fetch if the cache has nothing.
  const _rawVer = localStorage.getItem("currentBoqVersion");
  // version may be stored as "GWKS-3"/"RS-3" (set by /version-history) —
  // extract the plain numeric part so geoToberWallBuildUpApi/retainingWallsBuildUpApi
  // always receive a number, never the raw prefixed key.
  const _boqVersion = _rawVer ? getNumericBoqVersion((JSON.parse(_rawVer) as { version?: number | string })?.version ?? 1) : 1;
  const _effectiveVersion = _boqVersion === 0 ? 1 : _boqVersion;

  // Include flags and percentages — declared early so the state-fallback block below can use them
  const _getCheckbox = (key: string): boolean => {
    const v = localStorage.getItem(key);
    return v !== null ? (JSON.parse(v) as boolean) : false;
  };
  const _inclPrelims = _getCheckbox("groundworks_includePreliminariesDisplay");
  const _inclOhp = _getCheckbox("groundworks_includeOhpDisplay");
  const _inclDiscount = _getCheckbox("groundworks_includeDiscountDisplay");
  const _gwSettings = (stateToSave.globalSettings as unknown as Record<string, Record<string, number>>)?.groundworks;
  const _prelimPct = groundworksTotals?.preliminariesPercent ?? _gwSettings?.preliminaries ?? 0;
  const _ohpPct = groundworksTotals?.ohpPercent ?? _gwSettings?.overheadAndProfit ?? 0;
  const _discountPct = groundworksTotals?.discountPercent ?? _gwSettings?.discount ?? 0;

  let _retWallsSpecs: RetainingWallsBuildUpSpecification[] | undefined;
  let _geoWallsSpecs: GeoToberWallBuildUpSpecification[] | undefined;

  if (projectName) {
    // Fetch retaining walls fresh from the API FIRST, scoped to the exact version being
    // saved (_effectiveVersion) — the React Query cache is NOT invalidated by
    // saveRetainingWallsBuildUp (direct API call, not mutation hook), so it can hold
    // stale/wrong-version specs even after a successful build-up save. A cache lookup
    // keyed only by `["retainingWallsBuildUp", projectName]` (no version) previously
    // matched ANY cached version's specs via getQueriesData's prefix match, which could
    // silently feed a DIFFERENT version's wall dimensions into this save's Subsoil
    // Management "Walls" quantity than what the live Bill Summary (version-scoped via
    // useGetRetainingWallsBuildUp) was showing. Fetching directly here, same pattern as
    // geo walls below, ensures we "wait for" the latest persisted values for THIS version.
    try {
      const r = await retainingWallsBuildUpApi.get(projectName, _effectiveVersion);
      if (r?.specifications?.length) {
        _retWallsSpecs = r.specifications;
      }
    } catch { /* 404 or network error — fall back to cache then state */ }

    // Retaining walls cache fallback (only used when API returns nothing / 404) — scoped
    // to the exact version being saved, not just the project, for the same reason above.
    if (!_retWallsSpecs) {
      const _cachedRetWalls = queryClient.getQueriesData<RetainingWallsBuildUpGetResponse>({
        queryKey: ["retainingWallsBuildUp", projectName, _effectiveVersion],
      });
      for (const [, data] of _cachedRetWalls) {
        if (data?.specifications?.length) { _retWallsSpecs = data.specifications; break; }
      }
    }

    // For geo walls: always fetch fresh from API first — the React Query cache is NOT
    // invalidated by saveGeoToberWallBuildUp (direct API call, not mutation hook), so
    // it can hold stale specs with empty items even after a successful build-up save.
    // Fetching directly here ensures we "wait for" the latest persisted values.
    try {
      const g = await geoToberWallBuildUpApi.get(projectName, _effectiveVersion);
      if (g?.specifications?.length) {
        _geoWallsSpecs = g.specifications;
      }
    } catch { /* 404 or network error — fall back to cache then state */ }

    // Geo walls cache fallback (only used when API returns nothing / 404) — scoped to
    // the exact version being saved, not just the project, for the same reason above.
    if (!_geoWallsSpecs) {
      const _cachedGeoWalls = queryClient.getQueriesData<GeoToberWallBuildUpGetResponse>({
        queryKey: ["geoToberWallBuildUp", projectName, _effectiveVersion],
      });
      for (const [, data] of _cachedGeoWalls) {
        if (data?.specifications?.length) { _geoWallsSpecs = data.specifications; break; }
      }
    }

    // 3. If still empty (first-ever save, before saveBothWallsBuildUp has run),
    //    build specs directly from state using the same description→id mapping and
    //    item-library rate lookup that saveRetainingWallsBuildUp / saveGeoToberWallBuildUp use.
    //    This ensures wall amounts are included even on the very first save.
    if (!_retWallsSpecs?.length) {
      const stateSpecs = buildRetainingWallSpecsFromState({
        groundworksData: {
          retainingWallsTable: (stateToSave.groundworks as unknown as Record<string, Array<Record<string, number | string | null | undefined>>>)?.retainingWallsTable,
          wallsBuildUpData: (stateToSave.groundworks as unknown as Record<string, Record<string, Array<{id?: string; rate?: number; labour?: number}>>>)?.wallsBuildUpData,
          wallsBuildUpDimensions: (stateToSave.groundworks as unknown as Record<string, Record<string, Array<{description: string; length: number; width: number; height: number; net: number; unit: string}>>>)?.wallsBuildUpDimensions,
        },
        itemLibraryData,
        ohpPercent: _ohpPct,
        prelimsPercent: _prelimPct,
      });
      if (stateSpecs.length > 0) {
        _retWallsSpecs = stateSpecs;
        console.log("🧱 Built retaining wall specs from state for first-save calculation:", stateSpecs.length, "specs");
      }
    }
    if (!_geoWallsSpecs?.length) {
      const stateSpecs = buildGeoToberWallSpecsFromState({
        groundworksData: {
          geoToberWallTable: (stateToSave.groundworks as unknown as Record<string, Array<Record<string, number | string | null | undefined>>>)?.geoToberWallTable,
          geoToberWallBuildUpData: (stateToSave.groundworks as unknown as Record<string, Record<string, Array<{id?: string; rate?: number; labour?: number}>>>)?.geoToberWallBuildUpData,
          geoToberWallBuildUpDimensions: (stateToSave.groundworks as unknown as Record<string, Record<string, Array<{description: string; length: number; width: number; height: number; net: number; unit: string}>>>)?.geoToberWallBuildUpDimensions,
          geoToberWallDimensionValues: (stateToSave.groundworks as unknown as Record<string, Record<string, string>>)?.geoToberWallDimensionValues,
        },
        itemLibraryData,
        ohpPercent: _ohpPct,
        prelimsPercent: _prelimPct,
      });
      if (stateSpecs.length > 0) {
        _geoWallsSpecs = stateSpecs;
        console.log("📐 Built geo/tober wall specs from state for first-save calculation:", stateSpecs.length, "specs");
      }
    }
  }

  // Compute summary after specs are resolved so retaining walls and geowalls use
  // real API/cache data, not the state-based fallback.
  const groundworksSummary = computeGroundworksBoqSummary(stateToSave.groundworks, itemLibraryData, {
    retainingWalls: _retWallsSpecs,
    geoWalls: _geoWallsSpecs,
  });

  // Roads & Sewers counterpart of groundworksSummary above — this is the save
  // path every "Save & Continue" / "Calculate & Review" workflow button on
  // the R&S routes actually goes through (unlike createBoqPayload, which only
  // GroundworksReview.tsx's own save handlers call), so it must compute and
  // persist billOfQuantities[0].roadAndSewers.summary here too, or R&S saves
  // from any other workflow step silently never carry the summary tree.
  // Gated the same way storage.ts's own roadAndSewers.totals block is (R&S
  // job type selected) — an unselected R&S job type has no data to summarise.
  const _roadsAndSewersBaseSummary = stateToSave.jobTypes?.roadsAndSewers === true
    ? computeRoadsAndSewersBoqSummary(stateToSave, itemLibraryData)
    : undefined;
  const roadsAndSewersSummary = _roadsAndSewersBaseSummary
    ? {
        ..._roadsAndSewersBaseSummary,
        // Ad-hoc "Add Section"/"Add Item" extra rows (useBillSummaryExtraItems.tsx,
        // reused from groundworks as-is) merge into each domain's own computed
        // array — same convention groundworks' own cutFills/foundations/etc merges
        // below use.
        prelims: [..._roadsAndSewersBaseSummary.prelims, ...flattenCutFillsExtraItemsToBoqLines(_resolvedPrelimsExtraItems)],
        foulSewers: [..._roadsAndSewersBaseSummary.foulSewers, ...flattenCutFillsExtraItemsToBoqLines(_resolvedFoulSewersExtraItems)],
        pumpStation: [..._roadsAndSewersBaseSummary.pumpStation, ...flattenCutFillsExtraItemsToBoqLines(_resolvedPumpStationExtraItems)],
        risingMain: [..._roadsAndSewersBaseSummary.risingMain, ...flattenCutFillsExtraItemsToBoqLines(_resolvedRisingMainExtraItems)],
        stormSewers: [..._roadsAndSewersBaseSummary.stormSewers, ...flattenCutFillsExtraItemsToBoqLines(_resolvedStormSewersExtraItems)],
        pondsSwales: [..._roadsAndSewersBaseSummary.pondsSwales, ...flattenCutFillsExtraItemsToBoqLines(_resolvedPondsSwalesExtraItems)],
        cellularAttenuation: [..._roadsAndSewersBaseSummary.cellularAttenuation, ...flattenCutFillsExtraItemsToBoqLines(_resolvedCellularAttenuationExtraItems)],
        weholiteAttenuation: [..._roadsAndSewersBaseSummary.weholiteAttenuation, ...flattenCutFillsExtraItemsToBoqLines(_resolvedWeholiteAttenuationExtraItems)],
        concAttenuation: [..._roadsAndSewersBaseSummary.concAttenuation, ...flattenCutFillsExtraItemsToBoqLines(_resolvedConcAttenuationExtraItems)],
        boxCulverts: [..._roadsAndSewersBaseSummary.boxCulverts, ...flattenCutFillsExtraItemsToBoqLines(_resolvedBoxCulvertsExtraItems)],
        roads: [..._roadsAndSewersBaseSummary.roads, ...flattenCutFillsExtraItemsToBoqLines(_resolvedRoadsExtraItems)],
        mainServices: [..._roadsAndSewersBaseSummary.mainServices, ...flattenCutFillsExtraItemsToBoqLines(_resolvedMainServicesExtraItems)],
        substation: [..._roadsAndSewersBaseSummary.substation, ...flattenCutFillsExtraItemsToBoqLines(_resolvedSubstationExtraItems)],
        pos: [..._roadsAndSewersBaseSummary.pos, ...flattenCutFillsExtraItemsToBoqLines(_resolvedPosExtraItems)],
        compound: [..._roadsAndSewersBaseSummary.compound, ...flattenCutFillsExtraItemsToBoqLines(_resolvedCompoundExtraItems)],
        // Each Roads & Sewers Dynamic Bill Summary tab (useDynamicBillSummary.tsx /
        // ReviewCalculate's "+ Add Tab" control) gets its own summary key named
        // after the tab, same flat "key holds this domain's line-item array" shape
        // every fixed roadAndSewers.summary domain key already uses.
        ...serializeDynamicBillSummaryTabs(_resolvedRoadsAndSewersDynamicTabs),
      }
    : undefined;

  // Always recalculate using the freshly fetched wall specs so that geowall and
  // retaining-wall amounts are never omitted due to a stale React Query cache.
  // saveGeoToberWallBuildUp posts directly (not via the mutation hook) so the
  // cache is not invalidated after a build-up save — meaning the hook's tenderTotal
  // may exclude geowalls even when the API has valid specs. Re-running the
  // calculation here with fresh _geoWallsSpecs / _retWallsSpecs guarantees the
  // payload reflects the current persisted build-up data.
  // Percentages come from groundworksTotals when provided (honours UI flags).
  let computedGrandTotal: number;

  const _saResult = calculateSumAnalysisGrandTotal({
    groundworksData: stateToSave.groundworks,
    itemLibraryData,
    preliminariesPercent: _prelimPct,
    ohpPercent: _ohpPct,
    discountPercent: _discountPct,
    includePreliminaries: _inclPrelims,
    includeOhp: _inclOhp,
    includeDiscount: _inclDiscount,
    retWallsBuildUpSpecs: _retWallsSpecs,
    geoToberWallBuildUpSpecs: _geoWallsSpecs,
    enableLogging: false,
  });
  computedGrandTotal = _saResult.tenderTotal;

  const sharedGroundworksItemLibrary = buildGroundworksItemLibrary(itemLibraryData, {
    cutFills: stateToSave.groundworks?.cutFillsTable,
    foundations: stateToSave.groundworks?.foundationsTable,
    drainage: stateToSave.groundworks?.drainageTableStandard,
    plotDrives: stateToSave.groundworks?.plotDrivesTable,
    garden: stateToSave.groundworks?.gardenTable,
    plotPavings: stateToSave.groundworks?.plotPavingsTable,
    scaffold: stateToSave.groundworks?.scaffoldTable,
    plotService: stateToSave.groundworks?.plotServiceTable,
    retainingWalls: stateToSave.groundworks?.retainingWallsTable,
    geoToberWall: stateToSave.groundworks?.geoToberWallTable,
  }, {
    drainageDimensionValues: (stateToSave.groundworks as unknown as Record<string, unknown>)?.drainageDimensionValues as Record<string, string> | undefined,
    foundationsPrefixValues: (stateToSave.groundworks as unknown as Record<string, unknown>)?.foundationsPrefixValues as Record<string, string> | undefined,
    cutFillsPrefixValues: (stateToSave.groundworks as unknown as Record<string, unknown>)?.cutFillsPrefixValues as Record<string, string> | undefined,
    plotDrivesDimensionValues: (stateToSave.groundworks as unknown as Record<string, unknown>)?.plotDrivesDimensionValues as Record<string, string> | undefined,
    plotDrivesCodeOverrides: (stateToSave.groundworks as unknown as Record<string, unknown>)?.plotDrivesCodeOverrides as Record<string, string> | undefined,
    substructureBlockType: (stateToSave.groundworks as unknown as Record<string, unknown>)?.substructureBlockType as string | undefined,
    plotServiceConfig: stateToSave.groundworks?.plotServiceConfig,
    screenWallsLabel: stateToSave.groundworks?.wallsBuildUpConfig?.screenWallsLabel,
    eoPillarLabel: stateToSave.groundworks?.wallsBuildUpConfig?.eoPillarLabel,
  });

  // Build BOQ payload
  const _boqBillOfQuantitiesRaw = groundworksTotals
    ? basePayload.billOfQuantities?.map((boq: any, index: number) =>
        index === 0
          ? {
              ...boq,
              ...(roadsAndSewersSummary ? {
                roadAndSewers: { ...boq.roadAndSewers, summary: roadsAndSewersSummary },
              } : {}),
              groundworks: {
                ...boq.groundworks,
                totals: {
                  grandTotal: computedGrandTotal,
                },
                ohp: groundworksTotals.ohpPercent,
                preliminaries: groundworksTotals.preliminariesPercent,
                // Each Dynamic Bill Summary tab (useDynamicBillSummary.tsx /
                // GroundworksReview's "+ Add Tab" control) gets its own summary
                // key named after the tab — read straight off state so they
                // survive autosave, same as the rest of the summary tree.
                summary: {
                  ...groundworksSummary,
                  // Ad-hoc Cut & Fill / Foundations / Drainage extra items
                  // (useBillSummaryExtraItems.tsx) merge into their own
                  // computed array — see flattenCutFillsExtraItemsToBoqLines /
                  // flattenDrainageExtraItemsToBoqLines (dynamicBillSummarySerialization.ts).
                  cutFills: [
                    ...groundworksSummary.cutFills,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedCutFillsExtraItems),
                  ],
                  foundations: [
                    ...groundworksSummary.foundations,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedFoundationsExtraItems),
                  ],
                  drainage: [
                    ...groundworksSummary.drainage,
                    ...flattenDrainageExtraItemsToBoqLines(_resolvedDrainageExtraItems),
                  ],
                  plotDrives: [
                    ...groundworksSummary.plotDrives,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedPlotDrivesExtraItems),
                  ],
                  plotService: [
                    ...groundworksSummary.plotService,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedPlotServiceExtraItems),
                  ],
                  scaffold: [
                    ...groundworksSummary.scaffold,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedScaffoldExtraItems),
                  ],
                  plotPavings: [
                    ...groundworksSummary.plotPavings,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedPlotPavingsExtraItems),
                  ],
                  garden: [
                    ...groundworksSummary.garden,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedGardenExtraItems),
                  ],
                  retainingWalls: [
                    ...groundworksSummary.retainingWalls,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedRetainingWallsExtraItems),
                  ],
                  geoWalls: [
                    ...groundworksSummary.geoWalls,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedGeoWallsExtraItems),
                  ],
                  ...serializeDynamicBillSummaryTabs(_resolvedDynamicTabs),
                },
              },
            }
          : boq
      )
    : basePayload.billOfQuantities?.map((boq: any, index: number) =>
        index === 0
          ? {
              ...boq,
              ...(roadsAndSewersSummary ? {
                roadAndSewers: { ...boq.roadAndSewers, summary: roadsAndSewersSummary },
              } : {}),
              groundworks: {
                ...boq.groundworks,
                totals: {
                  ...boq.groundworks?.totals,
                  grandTotal: computedGrandTotal,
                },
                // Each Dynamic Bill Summary tab (useDynamicBillSummary.tsx /
                // GroundworksReview's "+ Add Tab" control) gets its own summary
                // key named after the tab — read straight off state so they
                // survive autosave, same as the rest of the summary tree.
                summary: {
                  ...groundworksSummary,
                  // Ad-hoc Cut & Fill / Foundations / Drainage extra items
                  // (useBillSummaryExtraItems.tsx) merge into their own
                  // computed array — see flattenCutFillsExtraItemsToBoqLines /
                  // flattenDrainageExtraItemsToBoqLines (dynamicBillSummarySerialization.ts).
                  cutFills: [
                    ...groundworksSummary.cutFills,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedCutFillsExtraItems),
                  ],
                  foundations: [
                    ...groundworksSummary.foundations,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedFoundationsExtraItems),
                  ],
                  drainage: [
                    ...groundworksSummary.drainage,
                    ...flattenDrainageExtraItemsToBoqLines(_resolvedDrainageExtraItems),
                  ],
                  plotDrives: [
                    ...groundworksSummary.plotDrives,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedPlotDrivesExtraItems),
                  ],
                  plotService: [
                    ...groundworksSummary.plotService,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedPlotServiceExtraItems),
                  ],
                  scaffold: [
                    ...groundworksSummary.scaffold,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedScaffoldExtraItems),
                  ],
                  plotPavings: [
                    ...groundworksSummary.plotPavings,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedPlotPavingsExtraItems),
                  ],
                  garden: [
                    ...groundworksSummary.garden,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedGardenExtraItems),
                  ],
                  retainingWalls: [
                    ...groundworksSummary.retainingWalls,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedRetainingWallsExtraItems),
                  ],
                  geoWalls: [
                    ...groundworksSummary.geoWalls,
                    ...flattenCutFillsExtraItemsToBoqLines(_resolvedGeoWallsExtraItems),
                  ],
                  ...serializeDynamicBillSummaryTabs(_resolvedDynamicTabs),
                },
              },
            }
          : boq
      );

  // A Groundworks-scoped save (route /groundworks, or an explicit GWKS override)
  // must leave Roads & Sewers untouched. state.roadsAndSewersTotals is only
  // populated by R&S pages, so from /groundworks it is missing/0 and would
  // overwrite the persisted R&S total with 0 — carry the persisted
  // roadAndSewers block (totals + summary) over verbatim instead.
  const _isGroundworksScopedSave =
    (versionPrefixOverride ?? getBoqVersionRoutePrefix()) === "GWKS";
  const _existingRoadAndSewers = (_existingBoqResult?.success && _existingBoqResult?.data)
    ? ((_existingBoqResult.data as any)?.billOfQuantities?.[0]?.roadAndSewers as Record<string, unknown> | undefined)
    : undefined;
  // Resolve the R&S grand total to carry: persisted BOQ value, then the live
  // state value, then the latest RS-N version's snapshot (the BOQ doc itself may
  // already have been zeroed by an earlier save). First positive value wins.
  let _carriedRsTotal = 0;
  if (_isGroundworksScopedSave) {
    const _persistedRs = Number((_existingRoadAndSewers as any)?.totals?.grandTotal) || 0;
    const _stateRs = Number(stateToSave.roadsAndSewersTotals?.grandTotal) || 0;
    _carriedRsTotal = _persistedRs || _stateRs;
    if (!_carriedRsTotal && projectName) {
      try {
        const _history = await billOfQuantitiesApi.getVersionHistory(projectName, opportunityNo);
        const _rsVersions = (_history.versions ?? [])
          .filter((v) => String(v.version).toUpperCase().startsWith("RS-"))
          .sort((a, b) => getNumericBoqVersion(b.version) - getNumericBoqVersion(a.version));
        _carriedRsTotal = Number(_rsVersions.find((v) => Number(v.snapshot?.roadAndSewersTotal) > 0)?.snapshot?.roadAndSewersTotal) || 0;
      } catch {
        // no history available — fall through with whatever we have
      }
    }
  }
  const boqBillOfQuantities = _isGroundworksScopedSave
    ? _boqBillOfQuantitiesRaw?.map((boq: any, index: number) =>
        index === 0
          ? {
              ...boq,
              roadAndSewers: {
                ...(_existingRoadAndSewers ?? {}),
                totals: { ...((_existingRoadAndSewers as any)?.totals ?? {}), grandTotal: _carriedRsTotal },
              },
            }
          : boq
      )
    : _boqBillOfQuantitiesRaw;

  // Always the sum of billOfQuantities[0].groundworks.totals.grandTotal and
  // .roadAndSewers.totals.grandTotal — see calculateBoqTenderTotal (src/utils/boqTotals.ts).
  const boqTenderTotal = calculateBoqTenderTotal(boqBillOfQuantities?.[0]);

  const boqPayload = groundworksTotals
    ? {
        ...basePayload,
        lastModifiedBy: userName || "Unknown",
        ...(opportunityNo && { opportunityNo }),
        totals: {
          tenderTotal: boqTenderTotal,
        },
        globalSettings: {
          ...basePayload.globalSettings,
          groundworks: {
            overheadAndProfit: groundworksTotals.ohpPercent,
            preliminaries: groundworksTotals.preliminariesPercent,
            discount: groundworksTotals.discountPercent,
            // TODO: Rename this key to 'discountAdded'
            discountAdded:
              groundworksTotals.discountAmount > 0 ? "true" : "false",
          },
        },
        billOfQuantities: boqBillOfQuantities,
        groundworksItemLibrary: sharedGroundworksItemLibrary,
      }
    : {
        ...basePayload,
        lastModifiedBy: userName || "Unknown",
        ...(opportunityNo && { opportunityNo }),
        totals: {
          ...(basePayload as any).totals,
          tenderTotal: boqTenderTotal,
        },
        billOfQuantities: boqBillOfQuantities,
        groundworksItemLibrary: sharedGroundworksItemLibrary,
      };

  console.log("📦 BOQ PAYLOAD (Built directly from groundworksTotals):", {
    tenderTotal: boqPayload.totals?.tenderTotal,
    grandTotal:
      boqPayload.billOfQuantities?.[0]?.groundworks?.totals?.grandTotal,
    preliminaries: boqPayload.globalSettings?.groundworks?.preliminaries,
    ohp: boqPayload.globalSettings?.groundworks?.overheadAndProfit,
    discount: boqPayload.globalSettings?.groundworks?.discount,
    isCorrect:
      boqPayload.totals?.tenderTotal !==
      boqPayload.billOfQuantities?.[0]?.groundworks?.totals?.grandTotal
        ? "✅ tenderTotal ≠ grandTotal (includes discount)"
        : "❌ tenderTotal = grandTotal (missing discount!)",
  });

  if (!projectName || !projectName.trim()) {
    console.error("❌ Cannot save BOQ: No project name found");
    throw new Error("Project name is required to save BOQ");
  }

  console.log(`📋 Project: ${projectName}`);

  // Whether the BOQ record already exists — reuses the fetch done up front
  // for the dynamicBillSummaryTabs/cutFillsExtraItems fallback above instead
  // of hitting the API a second time.
  const boqExists = !!(_existingBoqResult?.success && _existingBoqResult?.data);
  console.log(`📌 BOQ exists: ${boqExists}`);

  // Get version info from server
  let targetVersion = 1;

  const versionInfo = await getCurrentBoqVersion(projectName, opportunityNo, versionPrefixOverride);
  if (versionInfo) {
    targetVersion = versionInfo.version;
    console.log(`📌 Using existing version: ${targetVersion}`);
  } else {
    console.log(`📌 No versions exist yet, will PUT version ${targetVersion}`);
  }

  // Build version payload - use the corrected boqPayload wrapped with version metadata
  const versionPayload = {
    projectName: projectName,
    version: targetVersion,
    changeLog: changeLog,
    boq: boqPayload, // Use the corrected boqPayload (already has groundworksTotals values)
  };

  console.log("📦 VERSION PAYLOAD (Uses corrected boqPayload):", {
    structure: "wrapped in boq field",
    tenderTotal: versionPayload.boq?.totals?.tenderTotal,
    grandTotal:
      versionPayload.boq?.billOfQuantities?.[0]?.groundworks?.totals
        ?.grandTotal,
    preliminaries:
      versionPayload.boq?.globalSettings?.groundworks?.preliminaries,
    ohp: versionPayload.boq?.globalSettings?.groundworks?.overheadAndProfit,
    discount: versionPayload.boq?.globalSettings?.groundworks?.discount,
    matchesBoqPayload:
      versionPayload.boq?.totals?.tenderTotal === boqPayload.totals?.tenderTotal
        ? "✅ MATCHES boqPayload"
        : "❌ MISMATCH",
    isCorrect:
      versionPayload.boq?.totals?.tenderTotal !==
      versionPayload.boq?.billOfQuantities?.[0]?.groundworks?.totals?.grandTotal
        ? "✅ tenderTotal ≠ grandTotal (includes discount)"
        : "❌ tenderTotal = grandTotal (missing discount!)",
  });

  console.log(
    "🔥 CALLING BOTH ENDPOINTS SEQUENTIALLY - BOQ then BOQ-versions 🔥"
  );
  console.log(`📤 [1/2] BOQ endpoint: ${boqExists ? "PUT" : "POST"} /billofquantities${boqExists ? "/" + projectName : ""}`);
  console.log("   → tenderTotal:", boqPayload.totals?.tenderTotal);
  console.log(`📤 [2/2] BOQ-versions endpoint: PUT /boq-versions/${projectName}/${targetVersion}`);
  console.log("   → tenderTotal:", versionPayload.boq?.totals?.tenderTotal);

  // Post one after the other, waiting for each response — the caller keeps
  // its "Saving..." loader up for the whole span (it awaits this function),
  // and a failure at either step aborts the save rather than silently
  // persisting only half of it.
  try {
    if (boqExists) {
      console.log("🔵 [1/2] BOQ endpoint: UPDATE starting...");
      await billOfQuantitiesApi.update(projectName, boqPayload as unknown as BillOfQuantitiesRequest, opportunityNo);
      console.log("✅ [1/2] BOQ endpoint UPDATE completed");
    } else {
      console.log("🔵 [1/2] BOQ endpoint: CREATE starting...");
      await billOfQuantitiesApi.create(boqPayload as unknown as BillOfQuantitiesRequest, opportunityNo);
      console.log("✅ [1/2] BOQ endpoint CREATE completed");
    }
  } catch (error: any) {
    console.error("❌ [1/2] BOQ endpoint FAILED:", error?.message || error);
    throw new Error(`BOQ save failed: ${error?.message || "Unknown error"}`);
  }

  try {
    console.log(`🟢 [2/2] BOQ-versions endpoint: PUT starting for version ${targetVersion}...`);
    const versionResult = await billOfQuantitiesApi.updateVersion(
      projectName,
      targetVersion,
      versionPayload as any,
      opportunityNo,
      versionPrefixOverride,
    );
    console.log("✅ [2/2] BOQ-versions endpoint completed:", versionResult);
  } catch (error: any) {
    console.error("❌ [2/2] BOQ-versions endpoint FAILED:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(`BOQ-versions save failed: ${error?.message || "Unknown error"}`);
  }

  console.log("📊 FORCE SAVE RESULTS: both endpoints ✅ SUCCESS");

  // Invalidate React Query cache to trigger refetch on dashboard and on the
  // boq-version/boq-versions queries that drive live views like ScaffoldBillSummary's
  // dev-mode SummaryKey column (BOQ_QUERY_KEYS.all uses a different key prefix and
  // does not cover those).
  console.log("🔄 Invalidating BOQ query cache to trigger refetch...");
  try {
    queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
    queryClient.invalidateQueries({ queryKey: ["boq-versions", projectName] });
    queryClient.invalidateQueries({ queryKey: ["boq-version", projectName] });
    console.log("✅ Query cache invalidated successfully");
  } catch (error) {
    console.error("❌ Error invalidating query cache:", error);
  }

  console.log("🎉 FORCE SAVE COMPLETED");

  return { version: targetVersion };
};

/**
 * Get current BOQ version info from the server via getVersionHistory API.
 * Returns the latest (highest) version for the given project, or null if none exist.
 */
export const getCurrentBoqVersion = async (
  projectName: string,
  oppNo?: string,
  // Pages nested under /roads-sewers or /groundworks leave this undefined and
  // get route-based detection. Pages that aren't (e.g. /version-history, where
  // each row is independently RS-N or GWKS-N with no route to detect from)
  // must pass the prefix explicitly — otherwise scoping silently falls back to
  // "no prefix", mixing RS and GWKS entries together.
  prefixOverride?: "RS" | "GWKS" | null
): Promise<BoqVersionInfo | null> => {
  if (!projectName || !projectName.trim()) {
    return null;
  }
  try {
    const versionHistory = await billOfQuantitiesApi.getVersionHistory(projectName, oppNo);
    // Note: the API returns versions: [] when the project has exactly one
    // version (that lone version isn't listed as "history") — don't bail out
    // on an empty array here; getScopedCurrentVersionNumber falls back to
    // the response's top-level currentVersion for that case.
    const latestVersion = getScopedCurrentVersionNumber(versionHistory, prefixOverride);
    if (latestVersion === undefined) {
      return null;
    }
    return {
      projectName: versionHistory.projectName,
      version: latestVersion,
    };
  } catch (error) {
    console.error("Error fetching version history for project:", projectName, error);
  }
  return null;
};

/**
 * Update BOQ version via API
 * Automatically wraps the payload in the correct structure
 */
export const updateBoqVersion = async (
  projectName: string,
  version: number,
  boqPayload: BillOfQuantitiesRequest,
  changeLog: string = "Auto-saved changes",
  oppNo?: string
): Promise<void> => {
  console.log(`📤 Updating BOQ version: ${projectName} v${version}`);

  // Wrap in version payload structure
  const versionPayload = {
    projectName: projectName,
    boq: boqPayload, // Entire BOQ payload goes inside "boq" field
    changeLog: changeLog,
    version: version, // Include version number in payload
  };

  await billOfQuantitiesApi.updateVersion(
    projectName,
    version,
    versionPayload as any,
    oppNo,
  );
  console.log("✅ BOQ version updated successfully");
};

/**
 * Update current BOQ version from TenderState
 * Fetches version info from server and updates via API
 * If no version info exists but project has a name, creates a new BOQ
 */
export const updateCurrentBoqVersionFromState = async (
  tenderState: TenderState,
  changeLog: string = "Auto-saved changes"
): Promise<void> => {
  const projectName = tenderState.projectName || tenderState.siteAddress;
  if (!projectName || !projectName.trim()) {
    console.log("⚠️ No project name found - skipping API update");
    console.log(
      "💡 To enable API persistence, load or create a project from the dashboard"
    );
    return;
  }

  const boqPayload = transformToPayload(tenderState);
  const opportunityNo = tenderState.opportunityNo;
  const versionInfo = await getCurrentBoqVersion(projectName, opportunityNo);

  if (versionInfo) {
    const { version } = versionInfo;
    console.log(`📋 Found BOQ version info: ${projectName} v${version}`);
    console.log("📦 Updating both BOQ version and main BOQ via API...");

    try {
      await Promise.all([
        updateBoqVersion(projectName, version, boqPayload, changeLog, opportunityNo),
        billOfQuantitiesApi.update(projectName, boqPayload, opportunityNo),
      ]);
      console.log("✅ Both BOQ version and main BOQ updated successfully");
    } catch (error) {
      console.error("❌ Error updating BOQ or BOQ version:", error);
      try {
        console.log("🔄 Retrying BOQ version update...");
        await updateBoqVersion(projectName, version, boqPayload, changeLog, opportunityNo);
        console.log("✅ BOQ version updated");
      } catch (versionError) {
        console.error("❌ Failed to update BOQ version:", versionError);
      }
      try {
        console.log("🔄 Retrying main BOQ update...");
        await billOfQuantitiesApi.update(projectName, boqPayload, opportunityNo);
        console.log("✅ Main BOQ updated");
      } catch (boqError) {
        console.error("❌ Failed to update main BOQ:", boqError);
      }
    }
    return;
  }

  // No version exists yet - create new BOQ and version
  console.log(`📋 No versions found for project: ${projectName}`);
  console.log("📦 Creating both BOQ and BOQ version via API...");

  try {
    await Promise.all([
      (async () => {
        try {
          const existingBoq = await billOfQuantitiesApi.getByProject(projectName, opportunityNo as string | undefined);
          if (existingBoq.success && existingBoq.data) {
            console.log("📝 Updating existing BOQ...");
            await billOfQuantitiesApi.update(projectName, boqPayload, opportunityNo);
          } else {
            console.log("📝 Creating new BOQ...");
            await billOfQuantitiesApi.create(boqPayload, opportunityNo);
          }
          console.log("✅ Main BOQ updated successfully");
        } catch (error) {
          console.error("❌ Error updating main BOQ:", error);
          throw error;
        }
      })(),
      (async () => {
        try {
          console.log("📝 Saving BOQ version 1 via PUT...");
          await updateBoqVersion(projectName, 1, boqPayload, changeLog, opportunityNo);
          console.log("✅ BOQ version saved successfully");
        } catch (error) {
          console.error("❌ Error saving BOQ version:", error);
          throw error;
        }
      })(),
    ]);
    console.log("✅ Both BOQ and BOQ version created successfully");
  } catch (error) {
    console.error("❌ Error creating/updating BOQ and version:", error);
    throw error;
  }
};
